/**
 * Grade types — shapes flowing through the V1 grading pipeline per
 * Arch §2.6 and §2.7 and PRD §11.
 *
 *   GradeInput     — what the UI hands to GradeService (session config,
 *                    hand state, transcripts).
 *   GradeRequest   — what GradeService hands to the RubricGrader adapter
 *                    (static + dynamic context as the adapter sees it).
 *   RubricGrade    — the structured grade response per PRD §11.1 (10
 *                    criteria) + PRD §12 (prescribed default + session
 *                    focus feedback).
 *   GradeOutcome   — discriminated union the UI consumes: graded, pending
 *                    (will retry later), terminal (gave up).
 *
 * Grading granularity: **per hero decision**. A hand can have multiple
 * decisions on the same street (e.g., OOP checks → IP bets → OOP calls
 * gives OOP two decisions on the flop). Each decision is graded
 * independently because each has its own RTP narration, its own elapsed
 * time, and its own action context (e.g., SPR / Target / Inverse Sizing
 * only apply when hero is BETTING; Bluff-Catch Checklist only applies
 * when facing a bet).
 */

import type { ActionRecord, HandState } from './handState';
import type { Street } from './action';
import type { SessionConfig } from './session';

// =============================================================================
// Rubric criteria (PRD §11.1)
// =============================================================================

/** The 10 V1 rubric criteria. Order matches the wireframe display order. */
export const RUBRIC_CRITERIA = [
  'Position',
  'Default',
  'SPR',
  'Plan (CATO)',
  'Board Texture',
  'Target',
  'Range',
  'Inverse Sizing',
  'Hand Class',
  'Bluff Catch Checklist',
] as const;

export type RubricCriterion = (typeof RUBRIC_CRITERIA)[number];

export type CriterionVerdict = 'PASS' | 'PARTIAL' | 'MISSING' | 'NA';

export interface CriterionGrade {
  verdict: CriterionVerdict;
  /** Short feedback (one sentence). Surfaced in the rubric tab. */
  feedback: string;
}

// =============================================================================
// Prescribed default (PRD §11.2 — only criterion graded for correctness)
// =============================================================================

export type DefaultVerdict = 'FOLLOWED' | 'DEVIATED' | 'NA';

export interface PrescribedDefaultGrade {
  verdict: DefaultVerdict;
  /** Required when verdict === 'DEVIATED'; null otherwise. */
  rationale: string | null;
}

// =============================================================================
// Per-decision grade
// =============================================================================

/**
 * The kind of action hero took on this decision. Used for tab labels
 * (`Flop · Check`, `Turn · Bet`) and for action-context-driven verdict
 * defaults (e.g., Target is N/A when hero isn't betting).
 */
export type HeroActionKind =
  | 'check'
  | 'bet'
  | 'call'
  | 'raise'
  | 'fold'
  | 'allin';

export interface DecisionTimeScore {
  /** Elapsed seconds (decisionElapsedMs rounded). */
  seconds: number;
  /** Whether the decision was confirmed within the configured timer. */
  onTime: boolean;
}

export interface DecisionGrade {
  /**
   * Index into HandState.history identifying which hero ActionRecord
   * this grade is for. Lets the UI line up grades with their source
   * action without ambiguity.
   */
  actionIndex: number;
  /** Street on which the decision was made. */
  street: Street;
  /** What hero did on this decision. */
  actionKind: HeroActionKind;
  /**
   * All 10 criteria are present for every decision (verdict: 'NA' for
   * ones that don't apply to this action context — e.g., SPR / Target /
   * Inverse Sizing are N/A on non-betting decisions; Bluff Catch
   * Checklist is N/A unless hero is facing a bet on river / all-in /
   * 3-bet).
   */
  criteria: Record<RubricCriterion, CriterionGrade>;
  /**
   * Prescribed-default check for this decision. PRD §11.1 specifies
   * Default is N/A on the river — that's captured as `verdict: 'NA'`
   * for river decisions.
   */
  prescribedDefault: PrescribedDefaultGrade;
  /**
   * Per-decision time score. Computed client-side by GradeService from
   * ActionRecord.decisionElapsedMs against session.config.timerSeconds.
   * Undefined when the session had no timer configured or the action
   * record lacks timing data.
   */
  timeScore?: DecisionTimeScore;
}

// =============================================================================
// Full grade response
// =============================================================================

export interface RubricGrade {
  /**
   * Per-decision grades in history order. One entry per hero
   * ActionRecord in the hand. Empty for hands with no hero
   * decisions (rare — preflop fold).
   */
  decisions: DecisionGrade[];
  /**
   * Optional session-level focus feedback, populated only when the
   * session had a Session Focus set (PRD §7.3). Null otherwise.
   */
  sessionFocusFeedback: string | null;
  /**
   * Aggregate time score across all hero decisions in the hand
   * (PRD §11.2). Computed client-side from per-decision timeScores.
   */
  timeScore: {
    /** Number of decisions on-time. */
    onTime: number;
    /** Total number of decisions evaluated. */
    total: number;
    /** Average decision time in seconds, across the hand. */
    avgSeconds: number;
  };
}

// =============================================================================
// Service-layer types
// =============================================================================

/**
 * What the UI hands to GradeService. The service builds the
 * adapter-bound GradeRequest from this internally.
 */
export interface GradeInput {
  config: SessionConfig;
  state: HandState;
  /** Hero-only action records, in history order. */
  heroActions: ActionRecord[];
}

/**
 * The provider-agnostic shape a RubricGrader sees. Built by GradeService
 * from GradeInput. The static/dynamic split matches PRD §11.3 prompt-
 * caching strategy: staticContext is cached across calls (rubric +
 * strategy slice for the pot/position combo); dynamicContext is the
 * hand-specific payload that changes per call.
 */
export interface GradeRequest {
  rubricVersion: string;
  staticContext: {
    rubric: string;
    strategySlice: string;
  };
  dynamicContext: {
    gameState: object;
    /** Per-decision payload: street, action_kind, transcript per entry. */
    decisions: object;
  };
}

/**
 * UI-consumed result. Discriminated by `status` so callers can branch
 * cleanly between display, "still trying", and "give up + route to
 * couldn't-be-graded".
 */
export type GradeOutcome =
  | { status: 'graded'; grade: RubricGrade }
  | { status: 'pending'; willRetry: true }
  | {
      status: 'terminal';
      reason: 'cap_hit' | 'api_error' | 'invalid_response';
      /**
       * Optional developer-facing error message (e.g. the thrown error
       * from the adapter, the OpenAI HTTP body, etc.). Surfaced on the
       * CouldntBeGraded screen so dev failures are diagnosable without
       * digging into DevTools.
       */
      message?: string;
    };
